import Config from 'config';
import React from 'react';
import focus from '../../../../../classes/focusController';
import useContext from '../../../../../hooks/useContext';
import Field from '../../../../../JSApi/constants/Field';
import IProperty from '../../../../../JSApi/constants/IProperty';
import { IInputDatePickerField } from '../../../../../JSApi/fields/InputDatePickerField';
import { IApiaForm } from '../../../../../JSApi/types';
import store from '../../../../../store';
import { dataGridActions } from '../../../../../store/dataGridSlice/dataGridSlice';
import {
  fetchAjaxFieldEvents,
  setFieldEventSubmitForm,
  TFieldAjaxEventParams,
  TFieldAjaxEventResponsePayload,
} from '../../../../../store/fieldEventsSlice';
import { previousValidationActions } from '../../../../../store/previousValidationSlice';
import propsStore from '../../../../../store/propsStore/propsStore';
import { TFieldEvent, TFrmParent } from '../../../../../types';
import { buildSelector } from '../../../../../utils/dom';
import globalFocus from '../../../../../utils/globalFocus';
import { arrayOrArray } from '../../../../../utils/util';
import { dispatchNotifications } from '../../../../common/notifications/util';
import { ApiaComponentContextPrimitive } from '../../apiaComponent/ApiaComponentContext';
import { ApiaFormContext } from '../../page/ApiaForm';

const getApiaFormByFormId = (formId: string): IApiaForm | null => {
  const formName = document.getElementById(formId)?.dataset.formName;
  const frmParent = formId.substring(0, 1) as TFrmParent;
  if (formName) {
    return window.ApiaFunctions.getForm(formName, frmParent);
  }
  return null;
};

/**
 * Applies server-side property changes to React's source of truth. This must
 * not depend on the field having a discoverable DOM node, since hidden fields
 * and controls rendered through portals may not be available to the JS API.
 */
const setAjaxFieldProperty = (
  fieldId: string,
  propertyName: string,
  propertyValue: unknown,
) => {
  const normalizedValue =
    propertyValue === 'T'
      ? true
      : propertyValue === 'F'
        ? false
        : propertyValue;

  propsStore.updateField(fieldId, { [propertyName]: normalizedValue });

  if (
    propertyName === IProperty.PROPERTY_REQUIRED &&
    normalizedValue === false
  ) {
    propsStore.updateField(fieldId, { message: '' });
  }
};

export const setAjaxFormResponse = (
  response: TFieldAjaxEventResponsePayload,
) => {
  const forms = arrayOrArray(response.form);
  for (let i = 0; i < forms.length; i += 1) {
    const formId = forms[i].i;
    const form = forms[i];
    const apiaForm = getApiaFormByFormId(formId);

    /** Refresh the updated grids */
    const changedGrids = arrayOrArray(form.changedGrids.grdId);
    changedGrids.forEach(({ id }) => {
      void store.dispatch(
        dataGridActions.refresh({ gridId: `${formId}_${id}` }),
      );
    });

    if (!apiaForm) {
      console.warn(`Form with id ${formId} not found`);

      continue;
    }

    let fldId: string;
    // Iterate each field element in form element
    const ajaxFields = arrayOrArray(forms[i].field);
    for (let f = 0; f < ajaxFields.length; f += 1) {
      // got id of field to alter
      fldId = `${formId}_${ajaxFields[f].i}`;
      const apiField = apiaForm?.getFieldById(fldId);

      // Properties update React directly, even when the JS API cannot resolve
      // a hidden or portal-rendered field in the document.
      const fieldProperties = ajaxFields[f].field?.properties;
      if (ajaxFields[f].e !== undefined) {
        const ajaxProps = arrayOrArray(ajaxFields[f].e);
        for (let c = 0; c < ajaxProps.length; c += 1) {
          const properties = arrayOrArray(ajaxProps[c]?.f);
          for (let p = 0; p < properties.length; p += 1) {
            if (properties[p]?.i) {
              setAjaxFieldProperty(fldId, properties[p].i, properties[p].v);
            }
          }
        }
      } else if (fieldProperties) {
        Object.entries(fieldProperties).forEach(([name, value]) => {
          setAjaxFieldProperty(fldId, name, value);
        });
      }

      if (apiField) {
        // set VALUE to fldId
        if (ajaxFields[f].c !== undefined) {
          const { c } = ajaxFields[f];
          const valuesArray = arrayOrArray(
            typeof c !== 'string' ? c?.valueAttr : [],
          );
          let value: string = '';

          if (window.IS_EDIT_GRID_MODAL) {
            value = valuesArray[window.MODAL_INDEX].v;
          } else {
            value = valuesArray.reduce((reduced, current) => {
              return reduced + current.v;
            }, '');
          }

          if (value !== null && value !== undefined && valuesArray.length > 0) {
            if (
              apiField.fldType === Field.TYPE_INPUT &&
              apiField.valueType === 'D'
            ) {
              // value comes in seconds since 1970, cant do normal setValue
              const datePicker = apiField as IInputDatePickerField;
              datePicker.setDateValueInSeconds(parseInt(value, 10));
            } else {
              apiField.setValue(value, true);
            }
          }
          if (ajaxFields[f].c === '') {
            apiField.clearValue();
          }
        }
        // set field possible VALUES for select/multi and radio
        if (ajaxFields[f].possibleValues !== undefined) {
          const possibleValuesItems = arrayOrArray(
            ajaxFields[f].possibleValues,
          );
          const items = arrayOrArray(possibleValuesItems[0]?.item);
          if (items) {
            if (
              ['multiple', 'radio', 'select'].includes(
                apiField.fldType as string,
              )
            ) {
              propsStore.updateField(fldId, {
                field: {
                  ...propsStore.getFieldProps(fldId).field,
                  possibleValue: items.map((current) => ({
                    value: current.v,
                    label: current.text,
                  })),
                },
              });
            }
          }
        }

        if (
          ['tree', 'file'].includes(apiField.fldType as string) &&
          ajaxFields[f].field
        ) {
          propsStore.updateField(fldId, {
            field: ajaxFields[f].field,
            ...ajaxFields[f].field?.properties,
          });

          if (ajaxFields[f].field?.properties.required === false) {
            propsStore.updateField(fldId, {
              message: '',
            });
          }
        }
      }
    }

    // Formulario invisible
    if (
      form.v === 't' &&
      String(apiaForm.getProperty(IProperty.PROPERTY_FORM_HIDDEN)) !== 'true'
    ) {
      apiaForm.setProperty(IProperty.PROPERTY_FORM_HIDDEN, true);
    } else if (
      form.v === 'f' &&
      String(apiaForm.getProperty(IProperty.PROPERTY_FORM_HIDDEN)) === 'true'
    ) {
      apiaForm.setProperty(IProperty.PROPERTY_FORM_HIDDEN, false);
    }

    // Formulario cerrado
    if (
      form.c === 't' &&
      String(apiaForm.getProperty(IProperty.PROPERTY_FORM_CLOSED)) !== 'true'
    ) {
      apiaForm.closeForm();
    } else if (
      form.c === 'f' &&
      String(apiaForm.getProperty(IProperty.PROPERTY_FORM_CLOSED)) === 'true'
    ) {
      apiaForm.openForm();
    }
  }

  if (response.proccess?.p) {
    store.dispatch(
      previousValidationActions.setValue({
        form: 'executionData',
        name: 'cmbProPri',
        value: response.proccess.p,
        submitValue: response.proccess.p,
      }),
    );
  }
};
export async function fetchAjaxEvents(
  params: TFieldAjaxEventParams,
): Promise<boolean> {
  try {
    const data = await store.dispatch(fetchAjaxFieldEvents(params)).unwrap();
    if (data?.data) dispatchNotifications(data.data);

    if (data.success === true) {
      setAjaxFormResponse(data);
      return true;
    }
    return false;
  } catch (rejectedValue) {
    console.warn(rejectedValue);
    return false;
  }
}

function submitEvent(params: TFieldAjaxEventParams) {
  try {
    const element = globalFocus.last(
      ':not(body:not(#tinymce)):not(#ScreenLockFocusTrapper):not([data-action]):not(.overlay *):not(.actionButton)',
    );

    const selector = buildSelector(element as HTMLElement);

    if (selector) focus.afterReload({ selector });
  } catch (e) {
    console.warn(e);
  }

  store.dispatch(setFieldEventSubmitForm(params));
}
async function onServerEvents(
  isAjax: boolean,
  params: TFieldAjaxEventParams,
): Promise<boolean> {
  if (isAjax) {
    // AJAX SERVER EVENT
    const hasFetchedEvents = await fetchAjaxEvents(params);
    return hasFetchedEvents;
  }

  // SUBMIT SERVER EVENT
  submitEvent(params);
  return true;
}

export default function useServerEvents(): (
  event: TFieldEvent,
) => Promise<boolean> {
  const {
    field: { id, attId, serverEvents, entityBind: hasEntityBind },
    fieldId,
  } = useContext(ApiaComponentContextPrimitive);
  const { parsedFormId, frmParent } = useContext(ApiaFormContext);
  const getSubmitParams = React.useCallback(
    (evtId: number): TFieldAjaxEventParams => {
      const attributes = propsStore.getFieldProps(fieldId)?.attributes;
      let idx: number;
      if (Config.IS_EDIT_GRID_MODAL) {
        idx = Config.MODAL_INDEX;
      } else {
        idx = attributes?.index;
      }
      return {
        fldId: id,
        frmId: parsedFormId,
        frmParent,
        index: idx,
        evtId,
        attId,
      };
    },
    [attId, fieldId, frmParent, id, parsedFormId],
  );

  return async function fireServerEvents(
    event,
    additionalParameters?: Record<string, unknown>,
  ) {
    try {
      if (serverEvents?.length === 0 && hasEntityBind) {
        // do fire binding
        const params = {
          ...getSubmitParams(Config.EVENT_FLD_ONCHANGE),
          ...additionalParameters,
        };
        const hasBinded = await onServerEvents(true, params);
        return hasBinded;
      }
      if (serverEvents && serverEvents?.length > 0) {
        for (let i = 0; i < serverEvents.length; i += 1) {
          if (event === serverEvents[i].evtName) {
            const isAjax =
              serverEvents[i].isAjax &&
              !(
                serverEvents[i].evtName.startsWith('grid') &&
                serverEvents[i].evtName !== 'gridColumnSelect'
              );
            const params = {
              ...getSubmitParams(serverEvents[i].evtId),
              ...additionalParameters,
            };
            const hasFiredServerEvents = await onServerEvents(isAjax, params);
            return hasFiredServerEvents;
          }
        }
      }
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };
}
